(() => {
  if (window.wsHomeTrustMounted) return;
  window.wsHomeTrustMounted = true;
  const instances = new Map();
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  function mountResearchNote(root, opts) {
    const note = root.querySelector('[data-ws-research-note]');
    if (!note) return () => {};
    const trigger = note.querySelector('button');
    const text = note.querySelector('.ws-research-strip__note-text');
    const measure = () => {
      const travel = Math.max(0, (note.getBoundingClientRect().width - 44) / 2);
      note.style.setProperty('--ws-note-travel', `${travel}px`);
    };
    const setOpen = open => {
      note.dataset.open = String(open);
      trigger.setAttribute('aria-expanded', String(open));
      text.setAttribute('aria-hidden', String(!open));
    };
    note.dataset.ready = 'true';
    setOpen(false);
    measure();
    trigger.hidden = false;
    trigger.addEventListener('click', () => setOpen(note.dataset.open !== 'true'), opts);
    note.addEventListener('keydown', event => {
      if (event.key === 'Escape' && note.dataset.open === 'true') {
        setOpen(false);
        trigger.focus();
      }
    }, opts);
    const resize = new ResizeObserver(measure);
    resize.observe(note);
    return () => resize.disconnect();
  }
  function mount(scope = document) {
    scope.querySelectorAll('[data-ws-home-offer], [data-ws-research-strip]').forEach(root => {
      if (instances.has(root)) return;
      const controller = new AbortController();
      const opts = { signal: controller.signal };
      let cleanup = () => {};
      if (root.matches('[data-ws-home-offer]')) {
        const trigger = root.querySelector('button');
        const panel = root.querySelector('.ws-home-offer__panel');
        let animation;
        function setOpen(open) {
          animation?.cancel();
          trigger.setAttribute('aria-expanded', String(open));
          panel.hidden = false;
          if (reduce.matches) { panel.hidden = !open; return; }
          animation = panel.animate(open ? [{opacity:0,transform:'translateY(-5px)'},{opacity:1,transform:'translateY(0)'}] : [{opacity:1,transform:'translateY(0)'},{opacity:0,transform:'translateY(-5px)'}], {duration:180,easing:'ease-out'});
          animation.onfinish = () => {panel.hidden = !open;};
        }
        trigger.addEventListener('click', () => setOpen(trigger.getAttribute('aria-expanded') !== 'true'), opts);
        document.addEventListener('click', event => {if (!root.contains(event.target) && trigger.getAttribute('aria-expanded') === 'true') setOpen(false);}, opts);
        root.addEventListener('keydown', event => {if(event.key === 'Escape'){setOpen(false);trigger.focus();}}, opts);
        panel.querySelector('a').addEventListener('click', () => setOpen(false), opts);
        cleanup = () => animation?.cancel();
      } else {
        const disposeNote = mountResearchNote(root, opts);
        const viewport = root.querySelector('.ws-research-strip__window');
        const list = root.querySelector('.ws-research-strip__list');
        let visible = false, focused = false, held = false, touching = false, keyboardMode = true;
        let cycle = 0, phase = 0, speed = 60, raf = 0, last = null, resumeTimer = 0, manualUntil = 0;
        let pointer = null, suppressClickUntil = 0, writtenScroll = viewport.scrollLeft;
        const resumeDelay = 350;
        const wrap = value => cycle > 0 ? ((value % cycle) + cycle) % cycle : 0;
        const writeScroll = value => {
          viewport.scrollLeft = value;
          // Read back only to identify our scroll events, never to accumulate motion.
          writtenScroll = viewport.scrollLeft;
        };
        const stop = () => { cancelAnimationFrame(raf); raf = 0; last = null; };
        const frame = time => {
          const elapsed = last === null ? 0 : Math.min(time - last, 64);
          last = time;
          // Preserve subpixels even when the browser rounds each scrollLeft write.
          phase = wrap(phase + speed * elapsed / 1000);
          writeScroll(phase);
          raf = requestAnimationFrame(frame);
        };
        const sync = () => {
          root.dataset.focused = String(focused);
          const playing = cycle > 0 && visible && !focused && !held && !touching && !document.hidden && !reduce.matches && performance.now() >= manualUntil;
          root.dataset.playing = String(playing);
          if (playing && !raf) {
            // Resume at the user's final position, with no catch-up after a pause.
            phase = wrap(viewport.scrollLeft);
            writeScroll(phase);
            raf = requestAnimationFrame(frame);
          } else if (!playing) stop();
        };
        const pauseForInput = () => {
          manualUntil = performance.now() + resumeDelay;
          clearTimeout(resumeTimer);
          resumeTimer = setTimeout(sync, resumeDelay + 1);
          sync();
        };
        const measure = () => {
          cycle = list.getBoundingClientRect().width;
          speed = parseFloat(getComputedStyle(root).getPropertyValue('--ws-research-speed')) || 60;
          phase = reduce.matches ? 0 : wrap(viewport.scrollLeft);
          if (reduce.matches || (!held && !touching && !focused)) writeScroll(phase);
          last = null;
          sync();
        };
        // Native touch/trackpad scrolling, with mouse dragging as the desktop equivalent.
        viewport.addEventListener('pointerdown', event => {
          if (!event.isPrimary || event.button !== 0) return;
          held = true;
          keyboardMode = false;
          focused = false;
          pauseForInput();
          pointer = { id:event.pointerId, type:event.pointerType, x:event.clientX, y:event.clientY, scroll:viewport.scrollLeft, dragging:false };
        }, opts);
        window.addEventListener('pointermove', event => {
          if (!pointer || pointer.type !== 'mouse' || event.pointerId !== pointer.id) return;
          const dx = event.clientX - pointer.x, dy = event.clientY - pointer.y;
          if (!pointer.dragging && Math.abs(dx) > 6 && Math.abs(dx) > Math.abs(dy)) {
            pointer.dragging = true;
            viewport.setPointerCapture(event.pointerId);
            root.dataset.dragging = 'true';
          }
          if (!pointer.dragging) return;
          event.preventDefault();
          phase = wrap(pointer.scroll - dx);
          writeScroll(phase);
          pauseForInput();
        }, { ...opts, passive:false });
        const endPointer = event => {
          if (pointer && event.pointerId !== undefined && pointer.id !== event.pointerId) return;
          if (!held && !pointer) return;
          const ended = pointer;
          held = false; pointer = null;
          root.dataset.dragging = 'false';
          if (ended?.dragging) {
            suppressClickUntil = performance.now() + 350;
            if (viewport.hasPointerCapture(ended.id)) viewport.releasePointerCapture(ended.id);
          }
          pauseForInput();
        };
        // Native panning cancels pointer events before the finger leaves the screen.
        // Touch contact therefore owns a separate pause through touchend/cancel.
        viewport.addEventListener('touchstart', () => {
          touching = true;
          keyboardMode = false;
          focused = false;
          pauseForInput();
        }, { ...opts, passive:true });
        const endTouch = event => {
          if (!touching) return;
          touching = event.touches.length > 0;
          if (!touching) { endPointer(event); pauseForInput(); }
        };
        window.addEventListener('touchend', endTouch, { ...opts, passive:true });
        window.addEventListener('touchcancel', endTouch, { ...opts, passive:true });
        window.addEventListener('blur', event => {
          const wasTouching = touching;
          touching = false;
          endPointer(event);
          if (wasTouching) pauseForInput();
        }, opts);
        viewport.addEventListener('lostpointercapture', endPointer, opts);
        window.addEventListener('pointerup', endPointer, opts);
        window.addEventListener('pointercancel', endPointer, opts);
        viewport.addEventListener('dragstart', event => event.preventDefault(), opts);
        viewport.addEventListener('click', event => {
          if (performance.now() < suppressClickUntil) { event.preventDefault(); event.stopPropagation(); }
        }, { ...opts, capture:true });
        viewport.addEventListener('wheel', event => {
          if (Math.abs(event.deltaX) > 0 || event.shiftKey) {
            keyboardMode = false;
            focused = false;
            pauseForInput();
          }
        }, { ...opts, passive:true });
        viewport.addEventListener('scroll', () => {
          const current = viewport.scrollLeft;
          if (Math.abs(current - writtenScroll) < .01) return;
          phase = current;
          writtenScroll = current;
          // Only native input/momentum extends the idle window, not our own writes.
          if (!focused && !reduce.matches) pauseForInput();
        }, { ...opts, passive:true });
        const keyboardLink = target => keyboardMode && !!target?.matches('a:focus-visible') && root.contains(target) &&
          !!target.closest('.ws-research-strip__list:not([aria-hidden=true])');
        document.addEventListener('keydown', event => {
          keyboardMode = true;
          if (root.contains(event.target)) { focused = keyboardLink(event.target); sync(); }
        }, opts);
        root.addEventListener('focusin', event => {
          focused = keyboardLink(event.target);
          sync();
        }, opts);
        root.addEventListener('focusout', event => {
          focused = keyboardLink(event.relatedTarget);
          sync();
        }, opts);
        document.addEventListener('visibilitychange', sync, opts);
        const motionChanged = () => { measure(); sync(); };
        reduce.addEventListener('change', motionChanged, opts);
        const observer = new IntersectionObserver(entries => { visible = entries[0].isIntersecting; sync(); });
        const resize = new ResizeObserver(measure);
        observer.observe(root); resize.observe(list);
        cleanup = () => { stop(); clearTimeout(resumeTimer); observer.disconnect(); resize.disconnect(); disposeNote(); };
        measure(); sync();
      }
      instances.set(root, () => {controller.abort();cleanup();instances.delete(root);});
    });
  }
  if(document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(), {once:true}); else mount();
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', event => {for(const [root,dispose] of instances) if(event.target.contains(root)) dispose();});
})();
